import { useState, useRef, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { OrganizationCategory, shouldShowPerInspectionSignatureUI } from '@safetyhub/shared';
import {
  getInspectionSiteContacts,
  addInspectionSiteContacts,
  removeInspectionSiteContact,
  updateInspectionSiteContactSignature,
  type InspectionSiteContact,
} from '../../lib/inspections';
import { getSiteContacts, createSiteContact, updateSiteContact, type SiteContact } from '../../lib/tbm';
import { SignaturePadWithRef, type SignaturePadRef } from '../SignaturePad';
import { useToastStore } from '../../stores/toast';
import { SiteContactFormModal, SiteContactFormValue } from '../sites/SiteContactFormModal';
import { Badge, Button, Modal, TextButton } from '@safetyhub/ui';
import { MODAL_LAYER } from '../modalLayers';

export interface InspectionSiteContactsModalProps {
  inspectionId: string;
  siteId: string;
  companyId: string;
  organizationType: OrganizationCategory | null;
  open: boolean;
  onClose: () => void;
  onSkip?: () => void;
}

interface SignatureTargetContact {
  siteContactId: string;
  name: string;
}

export function InspectionSiteContactsModal({
  inspectionId,
  siteId,
  companyId,
  organizationType,
  open,
  onClose,
  onSkip,
}: InspectionSiteContactsModalProps) {
  const queryClient = useQueryClient();
  const { showToast } = useToastStore();
  const showSignatureUI = shouldShowPerInspectionSignatureUI(organizationType);

  // 서명 수집 모드 상태
  const [signatureTarget, setSignatureTarget] = useState<SignatureTargetContact | null>(null);
  const signaturePadRef = useRef<SignaturePadRef>(null);
  const [isSavingSignature, setIsSavingSignature] = useState(false);

  // 신규 담당자 추가 모달
  const [showNewContactForm, setShowNewContactForm] = useState(false);

  // 담당자 편집 모달 상태 (편집 대상만 — 입력값/저장중은 공통 모달이 소유)
  const [editingContact, setEditingContact] = useState<SiteContact | null>(null);

  // 현장 전체 담당자 목록
  const { data: allContacts = [], refetch: refetchAllContacts } = useQuery({
    queryKey: ['siteContacts', companyId, siteId],
    queryFn: () => getSiteContacts(companyId, siteId),
    enabled: open,
  });

  // 이미 선택된 점검 담당자 목록
  const { data: selectedContacts = [], refetch: refetchSelected } = useQuery({
    queryKey: ['inspectionSiteContacts', inspectionId],
    queryFn: () => getInspectionSiteContacts(inspectionId, companyId),
    enabled: open,
  });

  const selectedContactIds = useMemo(
    () => new Set(selectedContacts.map((c) => c.siteContactId)),
    [selectedContacts],
  );
  const signatureMap = useMemo(
    () => new Map(selectedContacts.map((c) => [c.siteContactId, c])),
    [selectedContacts],
  );

  // 선택 토글 — 모든 조직에서 체크박스로 통합
  const toggleMutation = useMutation({
    mutationFn: async ({ contactId, selected }: { contactId: string; selected: boolean }) => {
      if (selected) {
        await addInspectionSiteContacts(inspectionId, companyId, [contactId]);
      } else {
        await removeInspectionSiteContact(inspectionId, contactId, companyId);
      }
    },
    onMutate: async ({ contactId, selected }) => {
      await queryClient.cancelQueries({ queryKey: ['inspectionSiteContacts', inspectionId] });
      const previous = queryClient.getQueryData<InspectionSiteContact[]>(['inspectionSiteContacts', inspectionId]);
      queryClient.setQueryData<InspectionSiteContact[]>(
        ['inspectionSiteContacts', inspectionId],
        (old = []) => {
          if (selected) {
            if (old.some((c) => c.siteContactId === contactId)) return old;
            return [
              ...old,
              {
                inspectionId,
                siteContactId: contactId,
                signatureData: null,
                signatureMethod: null,
                signedAt: null,
                createdAt: new Date().toISOString(),
                siteContact: { id: contactId, name: '', phone: '', isActive: true },
              } as InspectionSiteContact,
            ];
          } else {
            return old.filter((c) => c.siteContactId !== contactId);
          }
        },
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous !== undefined) {
        queryClient.setQueryData(['inspectionSiteContacts', inspectionId], context.previous);
      }
      showToast('담당자 선택 중 오류가 발생했습니다.', 'error');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['inspectionSiteContacts', inspectionId] });
    },
  });

  const handleToggleContact = useCallback((contact: SiteContact) => {
    const isSelected = selectedContactIds.has(contact.id);
    toggleMutation.mutate({ contactId: contact.id, selected: !isSelected });
  }, [selectedContactIds, toggleMutation]);

  // 담당자 이름 클릭 → 편집 모달 오픈 (공통 SiteContactFormModal 수정 모드)
  const handleOpenEdit = useCallback((contact: SiteContact, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingContact(contact);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setEditingContact(null);
  }, []);

  // 담당자 수정 (공통 모달 onSubmit) — 모달이 전화번호 정규화·닫기 보호·포커스 트랩·무변경
  // 게이트를 소유한다. 넘어오는 value는 이미 trim된 상태다.
  const handleUpdateContact = useCallback(async (value: SiteContactFormValue) => {
    if (!editingContact) return;
    try {
      await updateSiteContact(companyId, siteId, editingContact.id, {
        name: value.name,
        phone: value.phone,
        department: value.department,
        position: value.position,
      });
      await refetchAllContacts();
      queryClient.invalidateQueries({ queryKey: ['siteContacts', companyId, siteId] });
      showToast('담당자 정보가 수정되었습니다.', 'success');
      handleCloseEdit();
    } catch {
      showToast('담당자 수정 중 오류가 발생했습니다.', 'error');
    }
  }, [editingContact, companyId, siteId, refetchAllContacts, queryClient, showToast, handleCloseEdit]);

  // 수정 모달 초기값 — editingContact identity에 고정해 편집 중 부모 리렌더에도 입력이 초기화되지
  // 않게 한다(SiteContactFormModal의 open/initialValue useEffect가 참조 변화 시 값을 재설정하므로).
  const editInitialValue = useMemo<SiteContactFormValue | undefined>(
    () =>
      editingContact
        ? {
            name: editingContact.name,
            phone: editingContact.phone,
            department: editingContact.department ?? '',
            position: editingContact.position ?? '',
          }
        : undefined,
    [editingContact],
  );

  // 서명 저장
  const handleSaveSignature = useCallback(async () => {
    if (!signatureTarget || !signaturePadRef.current) return;
    if (signaturePadRef.current.isEmpty()) {
      showToast('서명을 입력해 주세요.', 'error');
      return;
    }
    setIsSavingSignature(true);
    try {
      const signatureData = signaturePadRef.current.toDataURL('image/png');
      await updateInspectionSiteContactSignature(
        inspectionId,
        signatureTarget.siteContactId,
        companyId,
        { signatureData, signatureMethod: 'DRAWN' },
      );
      showToast('서명이 저장되었습니다.', 'success');
      refetchSelected();
      setSignatureTarget(null);
    } catch {
      showToast('서명 저장 중 오류가 발생했습니다.', 'error');
    } finally {
      setIsSavingSignature(false);
    }
  }, [signatureTarget, inspectionId, companyId, refetchSelected, showToast]);

  // 기존 담당자 목록에서 부서 자동완성 추출
  const departmentSuggestions = useMemo(
    () =>
      Array.from(
        new Set(
          allContacts
            .map((c) => c.department)
            .filter((d): d is string => Boolean(d && d.trim())),
        ),
      ),
    [allContacts],
  );

  // 신규 담당자 생성 (공통 모달 onSubmit)
  const handleCreateContact = useCallback(async (value: SiteContactFormValue) => {
    try {
      const created = await createSiteContact(companyId, siteId, {
        name: value.name,
        phone: value.phone,
        department: value.department,
        position: value.position,
        email: value.email,
      });
      await refetchAllContacts();
      await addInspectionSiteContacts(inspectionId, companyId, [created.id]);
      queryClient.invalidateQueries({ queryKey: ['inspectionSiteContacts', inspectionId] });
      setShowNewContactForm(false);
      showToast('담당자가 추가되었습니다.', 'success');
    } catch {
      showToast('담당자 추가 중 오류가 발생했습니다.', 'error');
    }
  }, [companyId, siteId, inspectionId, refetchAllContacts, queryClient, showToast]);

  const handleClose = useCallback(() => {
    setSignatureTarget(null);
    setShowNewContactForm(false);
    handleCloseEdit();
    onClose();
  }, [onClose, handleCloseEdit]);

  const handleSkip = useCallback(() => {
    setSignatureTarget(null);
    setShowNewContactForm(false);
    handleCloseEdit();
    if (onSkip) {
      onSkip();
    } else {
      onClose();
    }
  }, [onSkip, onClose, handleCloseEdit]);

  if (!open) return null;

  // 서명 패드 화면 — 목록 화면과 **다른 Modal 인스턴스**다(루트 요소 형이 달라 전환 때 다시
  // 마운트된다). 일부러 그렇게 둔다: 한 인스턴스로 내용만 바꾸면 눌린 「서명하기」 버튼이 사라지며
  // 포커스가 body로 떨어져 포커스 트랩 밖이 되는데, 다시 마운트하면 base가 새 dialog에 포커스를 준다.
  // 서명 저장 중에는 닫는 길 전부를 막는다(종전엔 저장 중에도 오버레이·✕·취소로 닫혔다).
  if (signatureTarget) {
    return (
      <Modal
        open
        onClose={() => setSignatureTarget(null)}
        title="서명 수집"
        size="md"
        closeOnOverlay={!isSavingSignature}
        closeOnEsc={!isSavingSignature}
        showCloseButton={!isSavingSignature}
        closeLocked={isSavingSignature}
        footer={
          <>
            <Button
              type="button"
              className="flex-1"
              onClick={() => setSignatureTarget(null)}
              disabled={isSavingSignature}
            >
              취소
            </Button>
            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={handleSaveSignature}
              disabled={isSavingSignature}
            >
              {isSavingSignature ? '저장 중...' : '서명 저장'}
            </Button>
          </>
        }
      >
        <p className="text-sm text-ink-500 mb-3">{signatureTarget.name}</p>
        <p className="text-sm text-ink-600 mb-4">
          아래 영역에 서명해 주세요.
        </p>
        <SignaturePadWithRef ref={signaturePadRef} height={160} />
      </Modal>
    );
  }

  return (
    <>
    <Modal
      open
      onClose={handleClose}
      title="현장 담당자 선택"
      size="md"
      footer={
        // 종전 하단 영역은 「신규 담당자 추가」 위 · 건너뛰기/완료 아래의 **세로 2단**이었다 —
        // base 푸터는 가로 한 줄이라 그 2단을 한 덩어리(w-full)로 넣는다.
        <div className="w-full space-y-3">
          {!showNewContactForm && (
            <button
              type="button"
              onClick={() => setShowNewContactForm(true)}
              className="w-full py-2 text-sm text-ink-600 border border-dashed border-ink-300 rounded-card-sm hover:bg-surface-hover hover:border-ink-400 transition-colors flex items-center justify-center gap-1.5"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              신규 담당자 추가
            </button>
          )}
          <div className="flex gap-3">
            <TextButton
              type="button"
              tone="neutral"
              onClick={handleSkip}
              className="flex-1 py-2.5 font-medium text-sm"
            >
              건너뛰기
            </TextButton>
            <Button
              type="button"
              variant="primary"
              className="flex-1"
              onClick={handleClose}
            >
              {showSignatureUI ? '완료' : '저장'}
            </Button>
          </div>
        </div>
      }
    >
      <p className="text-sm text-ink-500 mb-3">
        {showSignatureUI
          ? '담당자를 선택하고 서명을 받으세요.'
          : '이번 점검의 현장 담당자를 선택하세요.'}
      </p>

        {/* 담당자 목록 — 행이 패널 가장자리까지 가는 종전 배치를 본문 좌우 여백 상쇄로 유지한다 */}
        <div className="-mx-5">
          {allContacts.length === 0 ? (
            <div className="px-6 py-8 text-center text-ink-400 text-sm">
              등록된 현장 담당자가 없습니다.<br />아래 버튼으로 추가해 주세요.
            </div>
          ) : (
            <ul className="divide-y divide-line-soft">
              {allContacts.map((contact) => {
                const isSelected = selectedContactIds.has(contact.id);
                const junctionData: InspectionSiteContact | undefined = signatureMap.get(contact.id);
                const hasSigned = !!junctionData?.signatureData;

                return (
                  <li key={contact.id} className="px-6 py-3 flex items-center gap-3">
                    {/* 체크박스 — 모든 조직 공통 */}
                    <input
                      type="checkbox"
                      id={`contact-${contact.id}`}
                      checked={isSelected}
                      onChange={() => handleToggleContact(contact)}
                      disabled={toggleMutation.isPending}
                      className="w-4 h-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500 flex-shrink-0"
                    />
                    {/* 담당자 정보 — label 클릭 시 체크박스 토글 */}
                    <label
                      htmlFor={`contact-${contact.id}`}
                      className="flex-1 min-w-0 cursor-pointer"
                    >
                      <p className="text-sm font-medium text-ink-900 truncate">{contact.name}</p>
                      <p className="text-xs text-ink-500 truncate">
                        {[contact.position, contact.department, contact.phone]
                          .filter(Boolean)
                          .join(' · ')}
                      </p>
                    </label>
                    {/* 이름 편집 버튼 */}
                    <button
                      type="button"
                      onClick={(e) => handleOpenEdit(contact, e)}
                      className="flex-shrink-0 p-1 text-ink-300 hover:text-ink-500 transition-colors"
                      aria-label="담당자 정보 수정"
                      title="담당자 정보 수정"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    {/* 서명하기 버튼 — shouldShowPerInspectionSignatureUI 만족 + 체크된 담당자만 */}
                    {showSignatureUI && (
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        {isSelected && (
                          <Badge tone={hasSigned ? 'emerald' : 'slate'}>
                            {hasSigned ? '서명됨' : '미수집'}
                          </Badge>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (isSelected) {
                              setSignatureTarget({ siteContactId: contact.id, name: contact.name });
                            }
                          }}
                          disabled={!isSelected || toggleMutation.isPending}
                          className={`text-xs px-3 py-1.5 rounded-card-sm font-medium transition-colors ${
                            isSelected
                              ? hasSigned
                                ? 'bg-surface-hover text-ink-600 hover:bg-ink-200'
                                : 'bg-brand-600 text-white hover:bg-brand-700'
                              : 'bg-surface-hover text-ink-300 cursor-not-allowed'
                          }`}
                        >
                          {isSelected && hasSigned ? '재서명' : '서명하기'}
                        </button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
    </Modal>
      {/* 아래 둘은 이 모달 **위**에서 열린다 — 층은 여는 쪽이 정하므로 sub를 주입한다(`modalLayers.ts`).
          이 모달의 children이 아니라 형제로 둔다(children이면 React 트리상 이 패널의 클릭·키 핸들러를 거친다). */}
      {/* 신규 담당자 추가 (공통 모달) */}
      <SiteContactFormModal
        open={showNewContactForm}
        departmentSuggestions={departmentSuggestions}
        zIndexClass={MODAL_LAYER.sub}
        onSubmit={handleCreateContact}
        onCancel={() => setShowNewContactForm(false)}
      />
      {/* 담당자 정보 수정 (공통 모달 수정 모드) — 전화번호 정규화·닫기 보호·포커스 트랩 보유 */}
      {editingContact && (
        <SiteContactFormModal
          open
          title="담당자 정보 수정"
          showEmail={false}
          initialValue={editInitialValue}
          departmentSuggestions={departmentSuggestions}
          zIndexClass={MODAL_LAYER.sub}
          onSubmit={handleUpdateContact}
          onCancel={handleCloseEdit}
        />
      )}
    </>
  );
}

export default InspectionSiteContactsModal;
